import {
  AllowedFacetConditionOperator,
  FacetKey,
} from '@/features/analyze/types';
import { XMarkIcon } from '@heroicons/react/24/outline';

import { useSelectedFilterItems } from '@/features/connect-dots/hooks/useSelectedFilterItems';
import clsx from 'clsx';
import { Fragment, ComponentType, useRef } from 'react';
import { NodeType } from '@/features/connect-dots/types/cytoscape';
import { FilterItemMode } from '@/features/connect-dots/types/SelectedFilterItemsContextType';
import { useFilterConditionAtomValue } from '@/features/analyze/atoms/filterCondition.atom'; // 追加
import { useTranslationDictionary } from '@/lib/i18n/useTranslationDictionary';

type SelectedNodesListProps<K extends FacetKey> = {
  facetKey: K;
  modes: AllowedFacetConditionOperator<K>[];
  labelClassName: string;
  IconComponent?: ComponentType<{ className?: string }>;
  label: string;
  labelColor: string;
  backgroundColor?: string;
};

export function SelectedNodesList<K extends FacetKey>(
  props: SelectedNodesListProps<K>
) {
  return (
    <div className="p-2">
      <div>
        <div
          className={clsx(
            'mb-0.5 flex items-center gap-0.5 text-xs font-semibold uppercase',
            props.labelClassName
          )}
        >
          {props.IconComponent && (
            <props.IconComponent className="size-4 stroke-2" />
          )}
          {props.label}
        </div>

        <div
          className="rounded-tr-xl border-2"
          style={{
            borderColor: props.labelColor,
          }}
        >
          {props.modes.map((pMode, idx) => (
            <Fragment key={String(pMode)}>
              <SingleNode
                facetKey={props.facetKey}
                mode={pMode}
                labelClassName={props.labelClassName}
                IconComponent={props.IconComponent}
                label={props.label}
                labelColor={props.labelColor}
                backgroundColor={props.labelColor}
              />
              {idx < props.modes.length - 1 && (
                <hr className="mx-2 border-rw-light-gray" />
              )}
            </Fragment>
          ))}
        </div>
      </div>
    </div>
  );
}

// facetKey に対応する nodeType を取得する関数
const getNodeTypesForFacet = (facetKey: FacetKey): NodeType[] => {
  switch (facetKey) {
    case 'themes':
      return [NodeType.THEME];
    case 'countryTags':
      return [NodeType.REGION_ECONOMIC_UNION];
    case 'countries':
      return [NodeType.COUNTRY];
    case 'organizationTypes':
      return [NodeType.ORGANIZATION_TYPE];
    case 'topicTags':
      return [
        NodeType.ASSIST,
        NodeType.CROSS,
        NodeType.CROSS_ISSUE,
        NodeType.NORMAL,
        NodeType.SOCIAL_ISSUE,
      ];
    default:
      return [];
  }
};

function SingleNode<K extends FacetKey>({
  ...props
}: Omit<SelectedNodesListProps<K>, 'modes'> & {
  mode: AllowedFacetConditionOperator<K>;
}) {
  const { getLabel: getTopicTagLabel } = useTranslationDictionary('topic_tag');
  const { getLabel: getThemeLabel } = useTranslationDictionary('theme');
  const {
    selectedFilterItems: selectedNodes,
    removeNode,
    addNode,
  } = useSelectedFilterItems();
  const filterCondition = useFilterConditionAtomValue();
  const topicTags_and = filterCondition.topicTags?.and ?? [];
  topicTags_and.forEach((tag) => {
    addNode({
      label: getTopicTagLabel(tag),
      nodeType: NodeType.NORMAL, // THEME 以外であればなんでも良い
      mode: FilterItemMode.AND, // 'and' で追加
      name: tag, // タグ名を name として保存
      hasSelected: true, // 事前に選択されている
    });
  });

  const topicTags_or = filterCondition.topicTags?.or ?? [];
  topicTags_or.forEach((tag) => {
    addNode({
      label: getTopicTagLabel(tag),
      nodeType: NodeType.NORMAL, // THEME 以外であればなんでも良い
      mode: FilterItemMode.OR, // 'or' で追加
      name: tag, // タグ名を name として保存
      hasSelected: true, // 事前に選択されている
    });
  });

  const topicTags_not = filterCondition.topicTags?.not ?? [];
  topicTags_not.forEach((tag) => {
    addNode({
      label: getTopicTagLabel(tag),
      nodeType: NodeType.NORMAL, // THEME 以外であればなんでも良い
      mode: FilterItemMode.NOT, // 'not' で追加
      name: tag, // タグ名を name として保存
      hasSelected: true, // 事前に選択されている
    });
  });

  const themes_and = filterCondition.themes?.and ?? [];
  themes_and.forEach((tag) => {
    addNode({
      label: getThemeLabel(tag),
      nodeType: NodeType.THEME, // THEM
      mode: FilterItemMode.AND, // 'and' で追加
      name: tag, // タグ名を name として保存
      hasSelected: true, // 事前に選択されている
    });
  });

  const themes_or = filterCondition.themes?.or ?? [];
  themes_or.forEach((tag) => {
    addNode({
      label: getThemeLabel(tag),
      nodeType: NodeType.THEME, // THEME
      mode: FilterItemMode.OR, // 'or' で追加
      name: tag, // タグ名を name として保存
      hasSelected: true, // 事前に選択されている
    });
  });

  const countryTag_or = filterCondition.countryTags?.or ?? [];
  countryTag_or.forEach((tag) => {
    addNode({
      label: tag,
      nodeType: NodeType.REGION_ECONOMIC_UNION, // 国タグ
      mode: FilterItemMode.OR, // 'or' で追加
      name: tag, // タグ名を name として保存
      hasSelected: true, // 事前に選択されている
    });
  });

  const country_or = filterCondition.countries?.or ?? [];
  country_or.forEach((tag) => {
    addNode({
      label: tag,
      nodeType: NodeType.COUNTRY, // COUNTRY
      mode: FilterItemMode.OR, // 'or' で追加
      name: tag, // タグ名を name として保存
      hasSelected: true, // 事前に選択されている
    });
  });

  const country_not = filterCondition.countries?.not ?? [];
  country_not.forEach((tag) => {
    addNode({
      label: tag,
      nodeType: NodeType.COUNTRY, // COUNTRY
      mode: FilterItemMode.NOT, // 'not' で追加
      name: tag, // タグ名を name として保存
      hasSelected: true, // 事前に選択されている
    });
  });

  const organizationTypes_or = filterCondition.organizationTypes?.or ?? [];
  organizationTypes_or.forEach((tag) => {
    addNode({
      label: tag,
      nodeType: NodeType.ORGANIZATION_TYPE, // ORGANIZATION_TYPE
      mode: FilterItemMode.OR, // 'or' で追加
      name: tag, // タグ名を name として保存
      hasSelected: true, // 事前に選択されている
    });
  });

  // mode と facetKey でフィルタ
  const nodeTypesForFacet = getNodeTypesForFacet(props.facetKey);
  const nodes = selectedNodes.filter(
    (n) => n.mode === props.mode && nodeTypesForFacet.includes(n.nodeType)
  );

  const selectRef = useRef<HTMLDivElement>(null);

  return (
    <>
      <div className="relative w-full" ref={selectRef}>
        <div className="flex min-h-[3em] items-center gap-2">
          {/* モードラベル */}
          <div
            translate="no"
            className="pointer-events-none absolute left-2 top-1/2 z-10 min-w-10 -translate-y-1/2 rounded-tr px-1.5 py-1 text-center text-xs font-semibold uppercase text-white"
            style={{
              backgroundColor: props.backgroundColor ?? props.labelColor,
            }}
          >
            {String(props.mode)}
          </div>

          {/* タグラベル＋クリアボタン */}
          <div className="my-2 ml-[60px] flex flex-1 items-center">
            {' '}
            {/* モードラベル分左に余白 */}
            <div className="flex flex-1 flex-wrap gap-2">
              {nodes.map((node) => {
                switch (props.facetKey) {
                  case 'themes':
                    if (node.nodeType === NodeType.THEME) {
                      return node.hasSelected ? (
                        <span
                          key={node.label}
                          className="ml-1 rounded bg-gray-400 px-2 py-0.5 text-xs text-gray-800"
                        >
                          {node.label}
                        </span>
                      ) : (
                        <span
                          key={node.label}
                          className="mr-1 flex items-center rounded bg-[#3b8686] px-2 py-0.5 text-sm font-semibold text-white"
                        >
                          {node.label}
                          <button
                            className="ml-1 flex items-center justify-center rounded-full bg-white/80 p-0.5 text-sm hover:bg-white"
                            onClick={() => removeNode(node)}
                            aria-label="remove"
                            tabIndex={-1}
                            type="button"
                          >
                            <XMarkIcon className="size-2 invert" />
                          </button>
                        </span>
                      );
                    }
                    break;
                  case 'countryTags':
                    if (node.nodeType == NodeType.REGION_ECONOMIC_UNION) {
                      return node.hasSelected ? (
                        <span
                          key={node.label}
                          className="ml-1 rounded bg-gray-400 px-2 py-0.5 text-xs text-gray-800"
                        >
                          {node.label}
                        </span>
                      ) : null;
                    }
                    break;
                  case 'countries':
                    if (node.nodeType == NodeType.COUNTRY) {
                      return node.hasSelected ? (
                        <span
                          key={node.label}
                          className="ml-1 rounded bg-gray-400 px-2 py-0.5 text-xs text-gray-800"
                        >
                          {node.label}
                        </span>
                      ) : null;
                    }
                    break;
                  case 'organizationTypes':
                    if (node.nodeType == NodeType.ORGANIZATION_TYPE) {
                      return node.hasSelected ? (
                        <span
                          key={node.label}
                          className="ml-1 rounded bg-gray-400 px-2 py-0.5 text-xs text-gray-800"
                        >
                          {node.label}
                        </span>
                      ) : null;
                    }
                    break;

                  case 'topicTags':
                    if (
                      node.nodeType == NodeType.ASSIST ||
                      node.nodeType == NodeType.CROSS ||
                      node.nodeType == NodeType.CROSS_ISSUE ||
                      node.nodeType == NodeType.NORMAL ||
                      node.nodeType == NodeType.SOCIAL_ISSUE
                    ) {
                      return node.hasSelected ? (
                        <span
                          key={node.label}
                          className="ml-1 rounded bg-gray-400 px-2 py-0.5 text-xs text-gray-800"
                        >
                          {node.label}
                        </span>
                      ) : (
                        <span
                          key={node.label}
                          className="mr-1 flex items-center rounded bg-[#3b8686] px-2 py-0.5 text-sm font-semibold text-white"
                        >
                          {node.label}
                          <button
                            className="ml-1 flex items-center justify-center rounded-full bg-white/80 p-0.5 text-sm hover:bg-white"
                            onClick={() => removeNode(node)}
                            aria-label="remove"
                            tabIndex={-1}
                            type="button"
                          >
                            <XMarkIcon className="size-2 invert" />
                          </button>
                        </span>
                      );
                    }
                    break;
                  default:
                    return null;
                }
              })}
            </div>
          </div>
        </div>
      </div>
      {/* タグリストの下に線 */}
      <hr className="mx-2 border-rw-light-gray" />
    </>
  );
}
